<template>
  <div>
    <div class="text-subtitle-2 font-weight-bold mb-3">Datos de transferencia</div>
    <div v-if="loading" class="py-4 text-center">
      <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
    </div>
    <v-form v-else ref="bankForm" v-model="valid" @submit.prevent="save">
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-select
            v-model="form.bankId"
            :items="banks"
            item-text="name"
            item-value="id"
            label="Banco *"
            :rules="[required('El banco')]"
            outlined
            dense
            @change="form.bankAccountType = null"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="form.bankAccountType"
            :items="accountTypes"
            label="Tipo de cuenta *"
            :rules="[required('El tipo de cuenta')]"
            :disabled="!form.bankId"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="form.bankAccountNumber"
            label="Número de cuenta *"
            :rules="accountNumberRules"
            :hint="accountNumberHint"
            persistent-hint
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="form.bankAccountHolder"
            label="Titular *"
            :rules="holderRules"
            counter="120"
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="form.paymentEmail"
            label="Correo de pago *"
            :rules="emailRules"
            outlined
            dense
          ></v-text-field>
        </v-col>
      </v-row>
      <v-alert v-if="message" :type="messageType" dense outlined class="mb-3">{{ message }}</v-alert>
      <div class="d-flex justify-end">
        <v-btn type="submit" color="primary" depressed rounded :loading="saving" :disabled="!valid || saving">
          Guardar datos de transferencia
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import BanksService from '../services/banks.service';
import ClinicsService from '../services/clinics.service';

const ACCOUNT_NUMBER_PATTERN = /^\d{5,32}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Datos de transferencia de una clínica (125-clinic-billing-admin, US6), usados por la
// nómina de "Facturación de Clínicas". El GET devuelve la cuenta enmascarada, así que el
// número se vuelve a ingresar completo cada vez que se guardan los datos.
export default {
  name: 'ClinicBankDetailsSection',
  props: {
    clinicId: { type: String, required: true },
    clinicName: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      saving: false,
      valid: false,
      banks: [],
      maskedAccountNumber: '',
      form: this.emptyForm(),
      message: '',
      messageType: 'success',
      accountNumberRules: [
        (v) => !!(v && v.trim()) || 'El número de cuenta es obligatorio',
        (v) => ACCOUNT_NUMBER_PATTERN.test((v || '').trim()) || 'Solo dígitos, entre 5 y 32'
      ],
      holderRules: [
        (v) => !!(v && v.trim()) || 'El titular es obligatorio',
        (v) => (v || '').trim().length <= 120 || 'Máximo 120 caracteres'
      ],
      emailRules: [
        (v) => !!(v && v.trim()) || 'El correo de pago es obligatorio',
        (v) => EMAIL_PATTERN.test((v || '').trim()) || 'Debe ser un correo válido'
      ]
    };
  },
  computed: {
    selectedBank() {
      return this.banks.find((bank) => bank.id === this.form.bankId) || null;
    },
    accountTypes() {
      return (this.selectedBank && this.selectedBank.accountTypesAccepted) || [];
    },
    accountNumberHint() {
      return this.maskedAccountNumber
        ? `Cuenta registrada: ${this.maskedAccountNumber}. Ingrésala completa para guardar cambios.`
        : '';
    }
  },
  watch: {
    clinicId: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  methods: {
    emptyForm() {
      return {
        bankId: null,
        bankAccountType: null,
        bankAccountNumber: '',
        bankAccountHolder: this.clinicName || '',
        paymentEmail: ''
      };
    },
    required(field) {
      return (v) => !!v || `${field} es obligatorio`;
    },
    async load() {
      this.loading = true;
      this.message = '';
      this.form = this.emptyForm();
      this.maskedAccountNumber = '';
      const [banksRes, detailsRes] = await Promise.all([
        BanksService.getBanks('CL'),
        ClinicsService.getBankDetails(this.clinicId)
      ]);
      this.banks = banksRes.status === 'ok' ? banksRes.data || [] : [];
      this.applyDetails(detailsRes);
      if (banksRes.status !== 'ok') this.showMessage('No se pudo cargar la lista de bancos.', 'error');
      this.loading = false;
    },
    applyDetails(res) {
      // Una clínica sin datos bancarios puede venir como 404 o con campos en null.
      if (res.status !== 'ok') {
        if (res.httpCode !== 404) this.showMessage('No se pudieron cargar los datos de transferencia.', 'error');
        return;
      }
      const data = res.data || {};
      this.form = {
        bankId: data.bankId || null,
        bankAccountType: data.bankAccountType || null,
        bankAccountNumber: '',
        bankAccountHolder: data.bankAccountHolder || this.clinicName || '',
        paymentEmail: data.paymentEmail || ''
      };
      this.maskedAccountNumber = data.bankAccountNumber || '';
    },
    buildPayload() {
      return {
        bankId: this.form.bankId,
        bankAccountType: this.form.bankAccountType,
        bankAccountNumber: this.form.bankAccountNumber.trim(),
        bankAccountHolder: this.form.bankAccountHolder.trim(),
        paymentEmail: this.form.paymentEmail.trim()
      };
    },
    async save() {
      if (!this.$refs.bankForm.validate()) return;
      this.saving = true;
      this.message = '';
      const payload = this.buildPayload();
      const res = await ClinicsService.updateBankDetails(this.clinicId, payload);
      this.saving = false;
      if (res.status !== 'ok') {
        this.showMessage(
          res.httpCode === 400
            ? 'Revisa los datos bancarios: el backend los rechazó.'
            : 'No se pudieron guardar los datos de transferencia. Intenta nuevamente.',
          'error'
        );
        return;
      }
      this.maskedAccountNumber =
        (res.data && res.data.bankAccountNumber) || `****${payload.bankAccountNumber.slice(-4)}`;
      this.form.bankAccountNumber = '';
      this.$refs.bankForm.resetValidation();
      this.showMessage('Datos de transferencia guardados.', 'success');
    },
    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    }
  }
};
</script>
